// CRM — глобальный менеджер CRM-действий
// Доступен через window.crm

import morphdom from 'morphdom';
import {
	initDatepickers,
	initFormMulti,
	popupManager
} from '../_Common/app.js';

export class CRM {
	constructor() {
		this.invoiceId = null;
	}

	init() {
		const el = document.querySelector('[data-invoice-id]');
		if (el) {
			this.invoiceId = parseInt(el.getAttribute('data-invoice-id'));
		}

		const pageOrder = document.querySelector('.page.order');
		if (pageOrder) {
			this.bindEvents(pageOrder);
		}

		const pageOperation = document.querySelector('.page.operation');
		if (pageOperation) {
			this.bindEvents(pageOperation);
		}
	}

	// ─── Universal page refresh: chooses available context ────────────
	_refreshContext() {
		if (window.order && document.querySelector('.page.order')) {
			window.order.update();
			return;
		}
		if (window.operation && document.querySelector('.page.operation')) {
			window.operation.update();
			return;
		}
		// no-op for other pages
	}

	// ─── Toggle visibility ─────────────────────────────────────────────

	async showHideBlock(table, id, invoiceId) {
		await window.ajax.post('/ajax.php?mid=47', {
			action: 'showHideBlock',
			table: table,
			id: id,
			invoiceId: invoiceId || this.invoiceId
		});
		this._refreshContext();
	}

	// ─── Order actions ─────────────────────────────────────────────────

	async bookOrder(id) {
		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'bookOrder',
				id: id || this.invoiceId
			});

			if (response.success) {
				location.reload();
			} else {
				alert(response.error || 'Error issuing invoice');
			}
		} catch (error) {
			console.error('[CRM] bookOrder error:', error);
		}
	}

	async annulOrder(id) {
		const invoiceId = id || this.invoiceId;

		try {
			const preview = await window.ajax.post('/ajax.php?mid=47', {
				action: 'previewStatusChange',
				level: 'invoice',
				id: invoiceId,
				newStatus: 9
			});

			if (preview.changes && preview.changes.length > 0) {
				this._showInvoiceCancelConfirm(preview, invoiceId);
			} else {
				if (!confirm('Cancel this order?')) return;
				await this._executeAnnulOrder(invoiceId);
			}
		} catch (error) {
			console.error('[CRM] annulOrder error:', error);
		}
	}

	async _executeAnnulOrder(invoiceId, skipReload = false) {
		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'annulOrder',
				id: invoiceId
			});

			if (response.success) {
				if (!skipReload) location.reload();
			} else {
				alert(response.error || 'Error canceling order');
			}
		} catch (error) {
			console.error('[CRM] _executeAnnulOrder error:', error);
		}
	}

	_showInvoiceCancelConfirm(preview, invoiceId) {
		const hasSends = preview.sends.length > 0;

		const buttons = [];
		if (hasSends) buttons.push({ action: 'cancel-and-send', cls: 'btn--primary', icon: 'send', text: 'Cancel & Send All' });
		buttons.push({ action: 'cancel-only', cls: 'btn--Red', text: 'Cancel Order' });
		buttons.push({ action: 'dismiss', cls: 'btn--White-Gray300', text: 'Back' });

		const popup = this._buildStatusConfirmPopup(preview, {
			title: 'Cancel Order',
			description: 'All services and costing rows will be canceled:',
			buttons
		});

		this._openStatusConfirmPopup(popup);

		let handled = false;

		const closePopup = () => {
			if (handled) return;
			handled = true;
			popupManager.off('popupClosed', onClosed);
			popupManager.close('popup-status-confirm');
			popup.remove();
		};

		const onClosed = (name) => {
			if (name === 'popup-status-confirm') closePopup();
		};
		popupManager.on('popupClosed', onClosed);

		const executeAndClose = async (sendAfter) => {
			closePopup();
			const wantSends = sendAfter && preview.sends.length > 0;
			await this._executeAnnulOrder(invoiceId, wantSends);

			if (wantSends) {
				this._openSendsSequentially(preview.sends, preview.changes, () => {
					location.reload();
				});
			}
		};

		popup.querySelector('[data-action="cancel-and-send"]')?.addEventListener('click', () => executeAndClose(true));
		popup.querySelector('[data-action="cancel-only"]')?.addEventListener('click', () => executeAndClose(false));
		popup.querySelector('[data-action="dismiss"]')?.addEventListener('click', () => closePopup());
	}

	async deleteOrder(id) {
		if (!confirm('Are you sure you want to delete this draft?')) return;

		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'deleteOrder',
				id: id || this.invoiceId
			});

			if (response.success) {
				window.location.href = '/crm/';
			} else {
				alert(response.error || 'Error deleting order');
			}
		} catch (error) {
			console.error('[CRM] deleteOrder error:', error);
		}
	}

	// ─── Invoice field save ────────────────────────────────────────────

	async saveInvoiceField(field, value, invoiceId) {
		try {
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'saveInvoiceField',
				field: field,
				value: value,
				id: invoiceId || this.invoiceId
			});
			this._refreshContext();
		} catch (error) {
			console.error('[CRM] saveInvoiceField error:', error);
		}
	}

	async setMargin(invoiceId, value) {
		const num = Math.max(0, parseInt(value) || 0);
		await this.saveInvoiceField('NUM_MARGIN', num, invoiceId);
	}

	// ─── B2B / B2C switch ─────────────────────────────────────────────

	switchClientType(type, invoiceId) {
		// Табы только переключают видимость через CSS (:has selector)
		// Очистка альтернативного поля происходит при выборе агента/клиента
	}

	async selectAgent(agentId, invoiceId) {
		await this.saveInvoiceField('SLT_AGENTS', agentId, invoiceId);
		// Перезагрузить попап чтобы показать менеджеров нового агента
		await this.openInvoiceManager(invoiceId);
	}

	async selectClient(clientId, invoiceId) {
		await this.saveInvoiceField('SLT_CLIENTS', clientId, invoiceId);
		// Перезагрузить попап
		await this.openInvoiceManager(invoiceId);
	}

	// ─── Toggle hide names ────────────────────────────────────────────

	async toggleHideNames(invoiceId) {
		await this.saveInvoiceField('CHK_SHOW_NAMES', 'toggle', invoiceId);
	}

	// ─── Invoice Manager ───────────────────────────────────────────────

	async openInvoiceManager(invoiceId) {
		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'invoiceManager',
				id: invoiceId || this.invoiceId
			}, { dataType: 'html' });

			const wasOpen = !!document.querySelector('.popup-invoice-manager.active');
			this._updateOrInsertPopup('.popup-invoice-manager', response);

			if (!wasOpen) {
				const popup = document.querySelector('.popup-invoice-manager');
				if (popup) {
					this.bindEvents(popup);
					requestAnimationFrame(() => {
						popupManager.open('popup-invoice-manager');
					});
				}
			}
		} catch (error) {
			console.error('[CRM] openInvoiceManager error:', error);
		}
	}

	// ─── Service Editor ────────────────────────────────────────────────

	async openServiceEditor(table, serviceId, invoiceId, linkedDepartureId) {
		try {
			const params = {
				action: 'loadServiceEditor',
				table: table,
				id: serviceId,
				invoiceId: invoiceId || this.invoiceId
			};
			if (linkedDepartureId) params.linkedDepartureId = linkedDepartureId;

			const response = await window.ajax.post('/ajax.php?mid=47', params, { dataType: 'html' });

			const wasOpen = !!document.querySelector('.popup-service-editor.active');
			this._updateOrInsertPopup('.popup-service-editor', response);

			if (!wasOpen) {
				const popup = document.querySelector('.popup-service-editor');
				if (popup) {
					this.bindEvents(popup);
					requestAnimationFrame(() => {
						popupManager.open('popup-service-editor');
					});
				}
			}
		} catch (error) {
			console.error('[CRM] openServiceEditor error:', error);
		}
	}

	async saveServiceDates(table, serviceId, rangeValue) {
		// rangeValue = "15.01.2026 - 18.01.2026"
		const parts = rangeValue.split(' - ');
		const checkin = parts[0] || '';
		const checkout = parts[1] || '';

		try {
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'saveServiceDates',
				table: table,
				id: serviceId,
				checkin: checkin,
				checkout: checkout
			});
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'previewServicePrice',
				table: table,
				id: serviceId
			});
			this._refreshOpenServiceEditor(table, serviceId);
			this._refreshContext();
		} catch (error) {
			console.error('[CRM] saveServiceDates error:', error);
		}
	}

	async saveServiceField(table, serviceId, field, value) {
		try {
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'saveServiceField',
				table: table,
				id: serviceId,
				field: field,
				value: value
			});

			// Run preview: backend auto-applies when price is 0, otherwise the
			// next editor reload will render the indicator from XML.
			if (this._isPriceRelevant(field)) {
				await window.ajax.post('/ajax.php?mid=47', {
					action: 'previewServicePrice',
					table: table,
					id: serviceId
				});
			}

			this._refreshOpenServiceEditor(table, serviceId);
			this._refreshContext();
		} catch (error) {
			console.error('[CRM] saveServiceField error:', error);
		}
	}

	_isPriceRelevant(field) {
		const f = String(field).toUpperCase();
		return [
			'DTE_CHECKIN', 'DTE_CHECKOUT', 'SLT_HOTELS', 'SLT_ROOMS', 'CHK_EXB',
			'SLT_CARS', 'NUM_HOURS', 'SEL_TYPE', 'STR_TYPE',
			'SLT_TOURS', 'NUM_COUNT', 'CHK_GUIDE', 'MLT_TICKETS', 'NUM_DISCOUNT',
			'SLT_VISA_TYPES', 'CHK_URGENT',
			'SLT_RESTAURANTS', 'TME_START', 'DTE_DATE'
		].includes(f);
	}

	async applyServicePrice(btn) {
		const indicator = btn.closest('.popup-se__price-recalc');
		if (!indicator) return;
		const table = indicator.getAttribute('data-table');
		const serviceId = indicator.getAttribute('data-service-id');

		try {
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'applyServicePrice',
				table: table,
				id: serviceId
			});
			this._refreshOpenServiceEditor(table, serviceId);
			this._refreshContext();
		} catch (error) {
			console.error('[CRM] applyServicePrice error:', error);
		}
	}

	// saveServiceItemField теперь алиас saveServiceField (роутинг в PHP)
	async saveServiceItemField(table, serviceId, field, value) {
		return this.saveServiceField(table, serviceId, field, value);
	}

	// Toggle lock текстов + перезагрузка попапа
	async toggleDescLock(table, serviceId, value, invoiceId) {
		try {
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'saveServiceField',
				table: table,
				id: serviceId,
				field: 'NTD_LOCK_TEXT',
				value: value
			});
			await this.openServiceEditor(table, serviceId, invoiceId);
		} catch (error) {
			console.error('[CRM] toggleDescLock error:', error);
		}
	}

	// Сохранение текстов инвойса — ручная правка ставит lock
	async saveDescField(table, serviceId, field, value) {
		try {
			// Ставим lock при ручном изменении текста
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'saveServiceField',
				table: table,
				id: serviceId,
				field: 'NTD_LOCK_TEXT',
				value: 1
			});
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'saveServiceField',
				table: table,
				id: serviceId,
				field: field,
				value: value
			});
			this._refreshOpenServiceEditor(table, serviceId);
			this._refreshContext();
		} catch (error) {
			console.error('[CRM] saveDescField error:', error);
		}
	}

	// ─── Costing ───────────────────────────────────────────────────────

	async openCosting(table, serviceId) {
		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'loadCosting',
				table: table,
				id: serviceId
			}, { dataType: 'html' });

			const wasOpen = !!document.querySelector('.popup-costing.active');
			this._updateOrInsertPopup('.popup-costing', response);

			if (!wasOpen) {
				const popup = document.querySelector('.popup-costing');
				if (popup) {
					this.bindEvents(popup);
					requestAnimationFrame(() => {
						popupManager.open('popup-costing');
					});
				}
			}

			// First open auto-generates rows and updates service_items.NUM_COST.
			// Refresh the order page so the costing column reflects the new value.
			this._refreshContext();
		} catch (error) {
			console.error('[CRM] openCosting error:', error);
		}
	}

	async recalcCosting(table, serviceId) {
		if (!confirm('Recalculate all costing rows? Current rows will be replaced.'))
			return;

		try {
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'recalcCosting',
				table: table,
				id: serviceId
			});
			await this.openCosting(table, serviceId);
		} catch (error) {
			console.error('[CRM] recalcCosting error:', error);
		}
	}

	async saveCostingField(costingId, field, value) {
		try {
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'saveCostingField',
				id: costingId,
				field: field,
				value: value
			});
			// Перезагружаем костинг-попап и инвойс
			const popup = document.querySelector('.popup-costing');
			if (popup) {
				const table = popup.getAttribute('data-table');
				const serviceId = popup.getAttribute('data-service-id');
				if (table && serviceId) {
					await this.openCosting(table, serviceId);
				}
			}
			this._refreshContext();
		} catch (error) {
			console.error('[CRM] saveCostingField error:', error);
		}
	}

	async addCostingRow(table, serviceId) {
		try {
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'addCostingRow',
				table: table,
				id: serviceId
			});
			await this.openCosting(table, serviceId);
			this._refreshContext();
		} catch (error) {
			console.error('[CRM] addCostingRow error:', error);
		}
	}

	async deleteCostingRow(costingId, table, serviceId) {
		try {
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'deleteCostingRow',
				id: costingId
			});
			await this.openCosting(table, serviceId);
			this._refreshContext();
		} catch (error) {
			console.error('[CRM] deleteCostingRow error:', error);
		}
	}

	toggleCostingFiles(costingId, kind) {
		const drawer = document.getElementById('costing-drawer-' + costingId);
		if (!drawer) return;

		const isOpen = !drawer.hasAttribute('hidden');
		// Close all other drawers
		document.querySelectorAll('.popup-costing__drawer').forEach(d => {
			if (d !== drawer) {
				d.setAttribute('hidden', '');
				d.removeAttribute('data-open');
			}
		});

		if (isOpen) {
			drawer.setAttribute('hidden', '');
			drawer.removeAttribute('data-open');
			this._openCostingDrawer = null;
		} else {
			drawer.removeAttribute('hidden');
			drawer.setAttribute('data-open', '');
			this._openCostingDrawer = costingId;
			// Scroll the requested kind into view
			const section = drawer.querySelector(`[data-kind="${kind}"]`);
			if (section) section.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
	}

	async uploadCostingFile(costingId, kind, input) {
		if (!input.files || !input.files[0]) return;

		const formData = new FormData();
		formData.append('action', 'uploadCostingFile');
		formData.append('costingId', costingId);
		formData.append('kind', kind);
		formData.append('file', input.files[0]);

		try {
			const response = await fetch('/ajax.php?mid=47', { method: 'POST', body: formData });
			const data = await response.json();
			if (!data.success) {
				alert(data.error || 'Upload failed');
				return;
			}
			input.value = '';
			await this._refreshCosting();
		} catch (error) {
			console.error('[CRM] uploadCostingFile error:', error);
		}
	}

	async deleteCostingFile(fileId, kind) {
		if (!confirm('Delete this file?')) return;

		try {
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'deleteCostingFile',
				id: fileId,
				kind: kind
			});
			await this._refreshCosting();
		} catch (error) {
			console.error('[CRM] deleteCostingFile error:', error);
		}
	}

	async _refreshCosting() {
		const popup = document.querySelector('.popup-costing');
		if (!popup) return;
		const table = popup.getAttribute('data-table');
		const serviceId = popup.getAttribute('data-service-id');
		if (table && serviceId) {
			await this.openCosting(table, serviceId);
			// Restore open drawer after morphdom replaced HTML
			if (this._openCostingDrawer) {
				const drawer = document.getElementById('costing-drawer-' + this._openCostingDrawer);
				if (drawer) {
					drawer.removeAttribute('hidden');
					drawer.setAttribute('data-open', '');
				}
			}
		}
	}

	createCostingPayment(costingId) {
		// Stub: будущая интеграция с accounting2
		alert('Coming soon: this will create a payment record linked to costing row ' + costingId);
	}

	async changeCostingStatus(costingId, status, table, serviceId) {
		try {
			const preview = await window.ajax.post('/ajax.php?mid=47', {
				action: 'previewStatusChange',
				level: 'costing',
				id: costingId,
				newStatus: status
			});

			if (preview.sends && preview.sends.length > 0) {
				this._showStatusConfirm(preview, 'costing', costingId, status, table, serviceId);
			} else {
				await this._executeStatusChange('costing', costingId, status, table, serviceId);
			}
		} catch (error) {
			console.error('[CRM] changeCostingStatus error:', error);
			await this.openCosting(table, serviceId);
		}
	}

	async cancelService(table, serviceId) {
		try {
			const preview = await window.ajax.post('/ajax.php?mid=47', {
				action: 'previewStatusChange',
				level: 'service',
				id: 0,
				newStatus: 4,
				table: table,
				serviceId: serviceId
			});

			if (preview.changes && preview.changes.length > 0) {
				this._showStatusConfirm(preview, 'service', 0, 4, table, serviceId);
			} else {
				await this._executeStatusChange('service', 0, 4, table, serviceId);
			}
		} catch (error) {
			console.error('[CRM] cancelService error:', error);
		}
	}

	async _executeStatusChange(level, id, newStatus, table, serviceId) {
		try {
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'executeStatusChange',
				level: level,
				id: id,
				newStatus: newStatus,
				table: table,
				serviceId: serviceId
			});
			await this.openCosting(table, serviceId);
			this._refreshContext();
		} catch (error) {
			console.error('[CRM] _executeStatusChange error:', error);
			await this.openCosting(table, serviceId);
		}
	}

	_buildStatusConfirmPopup(preview, opts) {
		const title = opts.title;
		const description = opts.description;
		const buttons = opts.buttons;
		const labels = preview.statusLabels || {0: 'New', 1: 'Requested', 2: 'Confirmed', 3: 'Paid', 4: 'Canceled'};
		const channelIcons = {email: 'mail', wa: 'chat', app: 'phone_iphone'};

		const tpl = document.getElementById('tpl-status-confirm');
		const popup = tpl.content.cloneNode(true).firstElementChild;

		popup.querySelector('.popup-status-confirm__title').textContent = title || 'Status Change';

		const descEl = popup.querySelector('.popup-status-confirm__description');
		if (description) {
			descEl.textContent = description;
		} else {
			descEl.remove();
		}

		const changesContainer = popup.querySelector('.status-confirm__changes');
		const changeTpl = document.getElementById('tpl-status-change');
		preview.changes.forEach(c => {
			const row = changeTpl.content.cloneNode(true);
			row.querySelector('.status-confirm__name').textContent = c.name;
			const from = row.querySelector('.status-confirm__from');
			from.textContent = labels[c.from];
			from.classList.add('order__status-dot--' + c.from);
			const to = row.querySelector('.status-confirm__to');
			to.textContent = labels[c.to];
			to.classList.add('order__status-dot--' + c.to);
			changesContainer.appendChild(row);
		});

		const sendsContainer = popup.querySelector('.status-confirm__sends');
		if (preview.sends.length > 0) {
			const titleTpl = document.getElementById('tpl-status-sends-title');
			sendsContainer.appendChild(titleTpl.content.cloneNode(true));

			const sendTpl = document.getElementById('tpl-status-send');
			preview.sends.forEach(s => {
				const row = sendTpl.content.cloneNode(true);
				row.querySelector('.status-confirm__send-icon').textContent = channelIcons[s.channel] || s.icon;
				row.querySelector('.status-confirm__send-label').textContent = s.label;
				const recipient = row.querySelector('.status-confirm__recipient');
				if (s.recipientName) {
					recipient.textContent = s.recipientName;
				} else {
					recipient.remove();
				}
				sendsContainer.appendChild(row);
			});
		}

		const footer = popup.querySelector('.popup-status-confirm__footer');
		buttons.forEach(btn => {
			const el = document.createElement('button');
			el.type = 'button';
			el.className = 'btn btn--32 ' + btn.cls;
			if (btn.icon) {
				const icon = document.createElement('span');
				icon.className = 'material-symbols-sharp';
				icon.textContent = btn.icon;
				el.appendChild(icon);
				el.appendChild(document.createTextNode(' '));
			}
			el.appendChild(document.createTextNode(btn.text));
			el.dataset.action = btn.action;
			footer.appendChild(el);
		});

		return popup;
	}

	_openStatusConfirmPopup(popup) {
		const existing = document.querySelector('.popup-status-confirm');
		if (existing) {
			popupManager.close('popup-status-confirm');
			existing.remove();
		}

		document.body.appendChild(popup);
		popupManager.bindPopupEvents(popup);
		popupManager.open('popup-status-confirm');
		return popup;
	}

	_showStatusConfirm(preview, level, id, newStatus, table, serviceId) {
		const hasSends = preview.sends.length > 0;

		const buttons = [];
		if (hasSends) buttons.push({ action: 'change-and-send', cls: 'btn--primary', icon: 'send', text: 'Change & Send All' });
		buttons.push({ action: 'change-only', cls: 'btn--White-Gray300', text: 'Change Only' });
		buttons.push({ action: 'cancel', cls: 'btn--White-Gray300', text: 'Cancel' });

		const popup = this._buildStatusConfirmPopup(preview, {
			title: 'Status Change',
			buttons
		});

		this._openStatusConfirmPopup(popup);

		let handled = false;

		const closePopup = (reloadCosting = false) => {
			if (handled) return;
			handled = true;
			popupManager.off('popupClosed', onClosed);
			popupManager.close('popup-status-confirm');
			popup.remove();
			if (reloadCosting) {
				this.openCosting(table, serviceId);
			}
		};

		const executeAndClose = async (sendAfter) => {
			closePopup(false);
			await this._executeStatusChange(level, id, newStatus, table, serviceId);

			if (sendAfter && preview.sends.length > 0) {
				this._openSendsSequentially(preview.sends, preview.changes);
			}
		};

		const onClosed = (name) => {
			if (name === 'popup-status-confirm') {
				closePopup(true);
			}
		};
		popupManager.on('popupClosed', onClosed);

		popup.querySelector('[data-action="change-and-send"]')?.addEventListener('click', () => executeAndClose(true));
		popup.querySelector('[data-action="change-only"]')?.addEventListener('click', () => executeAndClose(false));
		popup.querySelector('[data-action="cancel"]')?.addEventListener('click', () => closePopup(true));
	}

	_openSendsSequentially(sends, changes, onComplete = null) {
		if (!sends || sends.length === 0) {
			if (onComplete) onComplete();
			return;
		}

		let index = 0;

		const openNext = () => {
			if (index >= sends.length) {
				if (onComplete) onComplete();
				return;
			}

			const s = sends[index];
			const invoiceId = changes[0]?.invoiceId;

			if (s.costingId) {
				window.sendManager.openSendCosting(s.sendType, s.table, s.serviceId, s.costingId, invoiceId);
			} else {
				window.sendManager.openSendService(s.sendType, s.table, s.serviceId, invoiceId);
			}

			index++;

			popupManager.on('popupClosed', function onSendClosed(name) {
				if (name === 'popup-send-email' || name === 'popup-send-wa' || name === 'popup-send-app') {
					popupManager.off('popupClosed', onSendClosed);
					setTimeout(() => openNext(), 300);
				}
			});
		};

		openNext();
	}

	// ─── Payments ──────────────────────────────────────────────────────

	async openPayments(invoiceId) {
		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'loadPayments',
				id: invoiceId || this.invoiceId
			}, { dataType: 'html' });

			const wasOpen = !!document.querySelector('.popup-payments.active');
			this._updateOrInsertPopup('.popup-payments', response);

			if (!wasOpen) {
				const popup = document.querySelector('.popup-payments');
				if (popup) {
					this.bindEvents(popup);
					requestAnimationFrame(() => {
						popupManager.open('popup-payments');
					});
				}
			}
		} catch (error) {
			console.error('[CRM] openPayments error:', error);
		}
	}

	async _refreshPayments() {
		const popup = document.querySelector('.popup-payments');
		if (!popup) return;
		const invoiceId = popup.getAttribute('data-invoice-id');
		if (invoiceId) await this.openPayments(invoiceId);
	}

	async submitPayment(btn) {
		const popup = btn.closest('.popup-payments');
		if (!popup) return;

		const invoiceId = popup.getAttribute('data-invoice-id');
		const form = btn.closest('.popup-payments__form');
		const sum = form.querySelector('[name="sum"]').value;

		if (!sum || parseInt(sum) <= 0) {
			alert('Please enter the amount');
			return;
		}

		const fileInput = form.querySelector('[name="file"]');

		const fd = new FormData();
		fd.append('action', 'addPayment');
		fd.append('invoiceId', invoiceId);
		fd.append('type', form.querySelector('[name="type"]').value);
		fd.append('sum', sum);
		fd.append('sumReceived', form.querySelector('[name="sumReceived"]').value);
		fd.append('currency', form.querySelector('[name="currency"]').value);
		fd.append('desc', form.querySelector('[name="desc"]').value);
		if (fileInput && fileInput.files && fileInput.files[0]) {
			fd.append('file', fileInput.files[0]);
		}

		try {
			const response = await fetch('/ajax.php?mid=47', { method: 'POST', body: fd });
			const data = await response.json();
			if (data.success) {
				await this._refreshPayments();
				this._refreshContext();
			} else {
				alert(data.error || 'Error adding payment');
			}
		} catch (error) {
			console.error('[CRM] submitPayment error:', error);
		}
	}

	onPaymentFilePick(input) {
		const label = input.closest('.popup-payments__file-pick');
		if (!label) return;
		const nameEl = label.querySelector('.popup-payments__file-name');
		if (!nameEl) return;
		if (input.files && input.files[0]) {
			nameEl.textContent = input.files[0].name;
		} else {
			nameEl.textContent = nameEl.dataset.empty || 'Choose file...';
		}
	}

	async uploadPaymentCott(paymentId, input) {
		if (!input.files || !input.files[0]) return;

		const fd = new FormData();
		fd.append('action', 'uploadPaymentCott');
		fd.append('id', paymentId);
		fd.append('file', input.files[0]);

		try {
			const response = await fetch('/ajax.php?mid=47', { method: 'POST', body: fd });
			const data = await response.json();
			if (data.success) {
				await this._refreshPayments();
			} else {
				alert(data.error || 'Upload failed');
			}
		} catch (error) {
			console.error('[CRM] uploadPaymentCott error:', error);
		}
	}

	async confirmPayment(paymentId) {
		const sumReceived = prompt('Received amount in SGD:');
		if (sumReceived === null) return;
		const value = parseInt(sumReceived);
		if (!value || value <= 0) {
			alert('Please enter a valid amount');
			return;
		}

		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'confirmPayment',
				id: paymentId,
				sumReceived: value
			});

			if (response.success) {
				await this._refreshPayments();
				this._refreshContext();
			} else {
				alert(response.error || 'Error confirming payment');
			}
		} catch (error) {
			console.error('[CRM] confirmPayment error:', error);
		}
	}

	async deletePayment(paymentId) {
		if (!confirm('Delete this payment record?')) return;

		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'deletePayment',
				id: paymentId
			});

			if (response.success) {
				await this._refreshPayments();
				this._refreshContext();
			} else {
				alert(response.error || 'Error deleting payment');
			}
		} catch (error) {
			console.error('[CRM] deletePayment error:', error);
		}
	}

	// ─── Travelers (Invoice Manager) ───────────────────────────────────

	async addTraveler(invoiceId) {
		try {
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'addTraveler',
				invoiceId: invoiceId || this.invoiceId
			});
			await this.openInvoiceManager(invoiceId);
			this._refreshOpenServiceEditor();
			this._refreshContext();
		} catch (error) {
			console.error('[CRM] addTraveler error:', error);
		}
	}

	async removeTraveler(travelerId, invoiceId) {
		try {
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'removeTraveler',
				id: travelerId,
				invoiceId: invoiceId || this.invoiceId
			});
			await this.openInvoiceManager(invoiceId);
			this._refreshOpenServiceEditor();
			this._refreshContext();
		} catch (error) {
			console.error('[CRM] removeTraveler error:', error);
		}
	}

	async addTravelerChild(invoiceId, age) {
		try {
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'addTraveler',
				invoiceId: invoiceId || this.invoiceId,
				agecat: 'Child',
				age: age
			});
			await this.openInvoiceManager(invoiceId);
			this._refreshOpenServiceEditor();
			this._refreshContext();
		} catch (error) {
			console.error('[CRM] addTravelerChild error:', error);
		}
	}

	async changeTravelerAge(travelerId, value, invoiceId) {
		try {
			if (value === '18+') {
				await window.ajax.post('/ajax.php?mid=47', {
					action: 'changeTraveler',
					id: travelerId,
					field: 'SEL_AGECAT',
					value: 'Adult',
					invoiceId: invoiceId || this.invoiceId
				});
			} else {
				await window.ajax.post('/ajax.php?mid=47', {
					action: 'changeTraveler',
					id: travelerId,
					field: 'SEL_AGECAT',
					value: 'Child',
					invoiceId: invoiceId || this.invoiceId
				});
				await window.ajax.post('/ajax.php?mid=47', {
					action: 'changeTraveler',
					id: travelerId,
					field: 'NUM_AGE',
					value: value,
					invoiceId: invoiceId || this.invoiceId
				});
			}
			await this.openInvoiceManager(invoiceId);
			this._refreshOpenServiceEditor();
		} catch (error) {
			console.error('[CRM] changeTravelerAge error:', error);
		}
	}

	async changeTraveler(travelerId, field, value, invoiceId) {
		try {
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'changeTraveler',
				id: travelerId,
				field: field,
				value: value,
				invoiceId: invoiceId || this.invoiceId
			});
			this._refreshContext();
		} catch (error) {
			console.error('[CRM] changeTraveler error:', error);
		}
	}

	// ─── Save departure flight date (saves to DTE_FLIGHTDATE + cascade)

	async saveTransferDate(table, serviceId, value) {
		return this.saveServiceField(table, serviceId, 'DTE_FLIGHTDATE', value);
	}

	// ─── Step hours (±0.5) for Disposal transfers ─────────────────────

	async stepHours(table, serviceId, delta) {
		const stepper = document.querySelector('.popup-service-editor .hours-stepper__value');
		if (!stepper) return;

		let current = parseFloat(stepper.textContent) || 0;
		current = Math.max(0, current + delta);
		stepper.textContent = current;

		await this.saveServiceField(table, serviceId, 'NUM_HOURS', current);
	}

	// ─── Save transfer flight (+ add to flights table) ───────────────

	async saveTransferFlight(table, serviceId, flightNo) {
		try {
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'saveTransferFlight',
				table: table,
				id: serviceId,
				flight: flightNo
			});
			this._refreshOpenServiceEditor(table, serviceId);
			this._refreshContext();
		} catch (error) {
			console.error('[CRM] saveTransferFlight error:', error);
		}
	}

	// ─── Toggle value in MLT_ field (comma-separated IDs) ────────────

	async toggleMultiField(table, serviceId, field, valueId) {
		try {
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'toggleMultiField',
				table: table,
				id: serviceId,
				field: field,
				valueId: valueId
			});
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'previewServicePrice',
				table: table,
				id: serviceId
			});
			this._refreshOpenServiceEditor(table, serviceId);
			this._refreshContext();
		} catch (error) {
			console.error('[CRM] toggleMultiField error:', error);
		}
	}

	// ─── Toggle traveler in service ───────────────────────────────────

	async toggleTraveler(table, serviceId, travelerId) {
		const checkbox = document.getElementById('se_trav_' + travelerId);
		const isAdding = checkbox ? checkbox.checked : true;

		// Pre-flight overflow check: by the time the onchange fires the
		// checkbox is already visually toggled, so we compare the live
		// DOM count against capacity. This catches racy double-clicks
		// where the server hasn't replied yet but the user clicks again.
		if (isAdding && checkbox) {
			const guestsBlock = checkbox.closest('.popup-se-guests');
			const cap = guestsBlock ? parseInt(guestsBlock.dataset.capacity, 10) : NaN;
			if (cap > 0) {
				const checked = guestsBlock.querySelectorAll('.popup-se-guests__item input.form-check__input:checked').length;
				if (checked > cap) {
					checkbox.checked = false;
					alert('Service capacity exceeded');
					return;
				}
			}
		}

		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'toggleServiceTraveler',
				table: table,
				id: serviceId,
				travelerId: travelerId
			});
			if (response && response.success === 0) {
				if (checkbox) checkbox.checked = !isAdding;
				alert(response.error || 'Failed to toggle guest');
				return;
			}
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'previewServicePrice',
				table: table,
				id: serviceId
			});
			this._refreshOpenServiceEditor(table, serviceId);
			this._refreshContext();
		} catch (error) {
			if (checkbox) checkbox.checked = !isAdding;
			console.error('[CRM] toggleTraveler error:', error);
		}
	}

	// ─── Delete service block ──────────────────────────────────────────

	async deleteBlock(table, id) {
		try {
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'deleteBlock',
				table: table,
				id: id
			});
			popupManager.closeAll();
			this._refreshContext();
		} catch (error) {
			console.error('[CRM] deleteBlock error:', error);
		}
	}

	// ─── Add service ───────────────────────────────────────────────────

	// ─── Add Service ───────────────────────────────────────────────────

	async addService(table, invoiceId) {
		// Reset edit mode (unless called from changeHotel)
		if (!this._editServiceId) {
			this._editServiceId = null;
			this._editInvoiceId = null;
		}

		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'loadAddServicePopup',
				table: table,
				invoiceId: invoiceId || this.invoiceId
			}, { dataType: 'html' });

			this._updateOrInsertPopup('.popup-add-service', response);

			const popup = document.querySelector('.popup-add-service');
			if (popup) {
				requestAnimationFrame(() => {
					popupManager.open('popup-add-service');
					// Auto-focus search after animation starts
					setTimeout(() => {
						const search = popup.querySelector('.popup-as__search-input');
						if (search) search.focus();
					}, 250);
				});
			}
		} catch (error) {
			console.error('[CRM] addService error:', error);
		}
	}

	// ─── Tours ─────────────────────────────────────────────────────────

	async selectTour(tourId, invoiceId) {
		const editServiceId = this._editServiceId || null;

		try {
			if (editServiceId) {
				// Edit mode — update tour
				await window.ajax.post('/ajax.php?mid=47', {
					action: 'updateServiceTour',
					table: 'custom_tours',
					id: editServiceId,
					tourId: tourId
				});

				this._editServiceId = null;
				popupManager.close('popup-add-service');
				this._refreshContext();

				setTimeout(() => {
					this.openServiceEditor('custom_tours', editServiceId, invoiceId);
				}, 300);
			} else {
				// Create mode
				const response = await window.ajax.post('/ajax.php?mid=47', {
					action: 'createServiceBlock',
					table: 'custom_tours',
					invoiceId: invoiceId || this.invoiceId,
					tourId: tourId
				});

				if (response.success) {
					popupManager.close('popup-add-service');
					this._refreshContext();

					if (response.serviceId) {
						setTimeout(() => {
							this.openServiceEditor('custom_tours', response.serviceId, invoiceId);
						}, 300);
					}
				}
			}
		} catch (error) {
			console.error('[CRM] selectTour error:', error);
		}
	}

	async createCustomTour(invoiceId) {
		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'createServiceBlock',
				table: 'custom_tours',
				invoiceId: invoiceId || this.invoiceId,
				isCustom: 1
			});

			if (response.success) {
				popupManager.close('popup-add-service');
				this._refreshContext();

				if (response.serviceId) {
					setTimeout(() => {
						this.openServiceEditor('custom_tours', response.serviceId, invoiceId);
					}, 300);
				}
			}
		} catch (error) {
			console.error('[CRM] createCustomTour error:', error);
		}
	}

	// Change tour for existing service
	async changeTour(serviceId, invoiceId) {
		this._editServiceId = serviceId;
		this._editInvoiceId = invoiceId || this.invoiceId;

		popupManager.close('popup-service-editor');
		await this.addService('custom_tours', invoiceId);
	}

	// ─── CIP / Services ────────────────────────────────────────────────

	async createCip(cipType, invoiceId) {
		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'createServiceBlock',
				table: 'cip',
				invoiceId: invoiceId || this.invoiceId,
				cipType: cipType
			});

			if (response.success) {
				popupManager.close('popup-add-service');
				this._refreshContext();

				if (response.serviceId) {
					setTimeout(() => {
						this.openServiceEditor('services', response.serviceId, invoiceId);
					}, 300);
				}
			}
		} catch (error) {
			console.error('[CRM] createCip error:', error);
		}
	}

	async createCipBoth(invoiceId) {
		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'createServiceBlock',
				table: 'cip',
				invoiceId: invoiceId || this.invoiceId,
				bothWays: 1
			});

			if (response.success) {
				popupManager.close('popup-add-service');
				this._refreshContext();

				// Store linked Departure ID
				this._linkedDepartureId = response.serviceId2 || null;
				this._linkedInvoiceId = invoiceId || this.invoiceId;

				// Open editor for Arrival, then inject button
				if (response.serviceId) {
					setTimeout(async () => {
						// Pass linkedDepartureId to openServiceEditor
						await this.openServiceEditor('services', response.serviceId, invoiceId, this._linkedDepartureId);
					}, 300);
				}
			}
		} catch (error) {
			console.error('[CRM] createCipBoth error:', error);
		}
	}

	// Copy travelers from Arrival to Departure, then open Departure editor
	async goToDeparture(table, arrivalId, departureId, invoiceId) {
		// Copy travelers
		try {
			await window.ajax.post('/ajax.php?mid=47', {
				action: 'copyServiceTravelers',
				table: table,
				fromId: arrivalId,
				toId: departureId
			});
		} catch (e) {
			console.warn('[CRM] Could not copy travelers:', e);
		}

		// Clear linked state
		this._linkedDepartureId = null;

		// Remove departure button before morphdom replaces content
		const depBtn = document.querySelector('.popup-se__go-departure');
		if (depBtn) depBtn.remove();

		// Open Departure editor (without linked button)
		await this.openServiceEditor(table, departureId, invoiceId);
		this._refreshContext();
	}

	// ─── Restaurant ────────────────────────────────────────────────────

	// Change restaurant in existing service
	async changeRestaurant(serviceId, invoiceId) {
		this._editServiceId = serviceId;
		this._editInvoiceId = invoiceId || this.invoiceId;

		popupManager.close('popup-service-editor');
		await this.addService('restaurant', invoiceId);
	}

	async createRestaurant(restaurantId, invoiceId) {
		const editServiceId = this._editServiceId || null;

		try {
			if (editServiceId) {
				// Edit mode — update restaurant
				await window.ajax.post('/ajax.php?mid=47', {
					action: 'updateServiceRestaurant',
					table: 'services',
					id: editServiceId,
					restaurantId: restaurantId
				});

				this._editServiceId = null;
				popupManager.close('popup-add-service');
				this._refreshContext();

				setTimeout(() => {
					this.openServiceEditor('services', editServiceId, invoiceId);
				}, 300);
			} else {
				// Create mode
				const response = await window.ajax.post('/ajax.php?mid=47', {
					action: 'createServiceBlock',
					table: 'restaurant',
					invoiceId: invoiceId || this.invoiceId,
					restaurantId: restaurantId
				});

				if (response.success) {
					popupManager.close('popup-add-service');
					this._refreshContext();

					if (response.serviceId) {
						setTimeout(() => {
							this.openServiceEditor('services', response.serviceId, invoiceId);
						}, 300);
					}
				}
			}
		} catch (error) {
			console.error('[CRM] createRestaurant error:', error);
		}
	}

	// ─── Tickets ───────────────────────────────────────────────────────

	async createTickets(invoiceId) {
		// Collect checked tickets
		const checkboxes = document.querySelectorAll('.popup-as__list--tickets .form-check__input:checked');
		const ids = Array.from(checkboxes).map(cb => cb.value);

		if (ids.length === 0) {
			alert('Please select at least one ticket');
			return;
		}

		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'createServiceBlock',
				table: 'tickets',
				invoiceId: invoiceId || this.invoiceId,
				ticketIds: ids.join(',')
			});

			if (response.success) {
				popupManager.close('popup-add-service');
				this._refreshContext();

				if (response.serviceId) {
					setTimeout(() => {
						this.openServiceEditor('services', response.serviceId, invoiceId);
					}, 300);
				}
			}
		} catch (error) {
			console.error('[CRM] createTickets error:', error);
		}
	}

	// ─── Service (Event/Item/Custom) ───────────────────────────────────

	async createService(serviceType, invoiceId) {
		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'createServiceBlock',
				table: 'service',
				invoiceId: invoiceId || this.invoiceId,
				serviceType: serviceType
			});

			if (response.success) {
				popupManager.close('popup-add-service');
				this._refreshContext();

				if (response.serviceId) {
					setTimeout(() => {
						this.openServiceEditor('services', response.serviceId, invoiceId);
					}, 300);
				}
			}
		} catch (error) {
			console.error('[CRM] createService error:', error);
		}
	}

	// ─── Visas ─────────────────────────────────────────────────────────

	async createVisa(visaTypeId, urgent, invoiceId) {
		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'createServiceBlock',
				table: 'visas',
				invoiceId: invoiceId || this.invoiceId,
				visaTypeId: visaTypeId,
				urgent: urgent
			});

			if (response.success) {
				popupManager.close('popup-add-service');
				this._refreshContext();

				if (response.serviceId) {
					setTimeout(() => {
						this.openServiceEditor('visas', response.serviceId, invoiceId);
					}, 300);
				}
			}
		} catch (error) {
			console.error('[CRM] createVisa error:', error);
		}
	}

	// ─── Transfers ─────────────────────────────────────────────────────

	async createTransfer(type, invoiceId) {
		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'createServiceBlock',
				table: 'transfer',
				invoiceId: invoiceId || this.invoiceId,
				transferType: type
			});

			if (response.success) {
				popupManager.close('popup-add-service');
				this._refreshContext();

				if (response.serviceId) {
					setTimeout(() => {
						this.openServiceEditor('transfer', response.serviceId, invoiceId);
					}, 300);
				}
			}
		} catch (error) {
			console.error('[CRM] createTransfer error:', error);
		}
	}

	// Change hotel for existing service — opens hotel list
	async changeHotel(serviceId, invoiceId) {
		this._editServiceId = serviceId;
		this._editInvoiceId = invoiceId || this.invoiceId;

		// Close service editor first
		popupManager.close('popup-service-editor');
		await this.addService('accoms', invoiceId);
	}

	// Change room for existing service — opens rooms for current hotel
	async changeRoom(serviceId, hotelId, invoiceId) {
		this._editServiceId = serviceId;
		this._editInvoiceId = invoiceId || this.invoiceId;

		// Close service editor first
		popupManager.close('popup-service-editor');

		const response = await window.ajax.post('/ajax.php?mid=47', {
			action: 'loadAddServicePopup',
			table: 'accoms',
			invoiceId: invoiceId || this.invoiceId
		}, { dataType: 'html' });

		this._updateOrInsertPopup('.popup-add-service', response);

		const popup = document.querySelector('.popup-add-service');
		if (popup) {
			requestAnimationFrame(() => {
				popupManager.open('popup-add-service');
				// Jump directly to rooms
				this.selectHotelForRoom(hotelId, invoiceId);
			});
		}
	}

	// Filter hotel list by search text
	filterAddServiceList(searchText) {
		const items = document.querySelectorAll('.popup-as__item, .popup-as__ticket-item');
		const text = searchText.toLowerCase().trim();

		items.forEach(item => {
			const name = (item.dataset.name || '').toLowerCase();
			if (!text || name.includes(text)) {
				item.classList.remove('popup-as__item--hidden');
			} else {
				item.classList.add('popup-as__item--hidden');
			}
		});
	}

	// Filter by buttons (stars, place, letter, tag)
	_addServiceFilters = { stars: 'all', place: 'all', letter: 'all', tag: 'all' };

	filterAddService(filterType, value, btn) {
		this._addServiceFilters[filterType] = value;

		// Toggle active button in group
		const group = btn.closest('.popup-as__filter-group');
		if (group) {
			group.querySelectorAll('.popup-as__filter-btn').forEach(b => b.classList.remove('popup-as__filter-btn--active'));
			btn.classList.add('popup-as__filter-btn--active');
		}

		// Apply all filters
		const f = this._addServiceFilters;
		const items = document.querySelectorAll('.popup-as__item');

		items.forEach(item => {
			let show = true;

			if (f.stars !== 'all' && item.dataset.stars !== f.stars) show = false;
			if (f.place !== 'all' && item.dataset.place !== f.place) show = false;
			if (f.letter !== 'all' && item.dataset.letter !== f.letter) show = false;

			// Tag filter — comma-separated IDs in data-tags
			if (f.tag !== 'all' && item.dataset.tags) {
				const tags = item.dataset.tags.split(',');
				if (!tags.includes(f.tag)) show = false;
			} else if (f.tag !== 'all' && !item.dataset.tags) {
				show = false;
			}

			// Also respect search input
			const search = document.querySelector('.popup-as__search-input');
			if (search && search.value.trim()) {
				const name = (item.dataset.name || '').toLowerCase();
				if (!name.includes(search.value.toLowerCase().trim())) show = false;
			}

			item.classList.toggle('popup-as__item--hidden', !show);
		});
	}

	// Select hotel → load rooms
	async selectHotelForRoom(hotelId, invoiceId) {
		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'loadHotelRooms',
				id: hotelId
			}, { dataType: 'html' });

			// Hide hotel list, show rooms
			const popup = document.querySelector('.popup-add-service');
			if (!popup) return;

			const list = popup.querySelector('.popup-as__list');
			const filters = popup.querySelector('.popup-as__filters');
			const search = popup.querySelector('.popup-as__search');
			const rooms = popup.querySelector('.popup-as__rooms');

			if (list) list.style.display = 'none';
			if (filters) filters.style.display = 'none';
			if (search) search.style.display = 'none';
			if (rooms) {
				rooms.innerHTML = response;
				rooms.style.display = '';
			}
		} catch (error) {
			console.error('[CRM] selectHotelForRoom error:', error);
		}
	}

	// Back to hotel list from rooms
	backToHotelList() {
		console.log('[CRM] backToHotelList called');
		const popup = document.querySelector('.popup-add-service');
		if (!popup) { console.log('[CRM] popup not found'); return; }

		const list = popup.querySelector('.popup-as__list');
		const filters = popup.querySelector('.popup-as__filters');
		const search = popup.querySelector('.popup-as__search');
		const rooms = popup.querySelector('.popup-as__rooms');

		if (list) list.style.display = '';
		if (filters) filters.style.display = '';
		if (search) search.style.display = '';
		if (rooms) {
			rooms.style.display = 'none';
			rooms.innerHTML = '';
		}

		// Re-focus search
		const searchInput = popup.querySelector('.popup-as__search-input');
		if (searchInput) searchInput.focus();
	}

	// Select room → create new block or update existing
	async selectRoom(roomId, hotelId) {
		const popup = document.querySelector('.popup-add-service');
		const invoiceId = popup ? popup.dataset.invoiceId : this.invoiceId;
		const table = popup ? popup.dataset.table : 'accoms';
		const editServiceId = this._editServiceId || null;

		try {
			if (editServiceId) {
				// Edit mode — update existing block
				await window.ajax.post('/ajax.php?mid=47', {
					action: 'updateServiceHotelRoom',
					table: table,
					id: editServiceId,
					hotelId: hotelId,
					roomId: roomId
				});

				this._editServiceId = null;
				this._editInvoiceId = null;

				popupManager.close('popup-add-service');
				this._refreshContext();

				// Reopen service editor with updated data
				setTimeout(() => {
					this.openServiceEditor(table, editServiceId, invoiceId);
				}, 300);
			} else {
				// Create mode — new block
				const response = await window.ajax.post('/ajax.php?mid=47', {
					action: 'createServiceBlock',
					table: table,
					invoiceId: invoiceId,
					hotelId: hotelId,
					roomId: roomId
				});

				if (response.success) {
					popupManager.close('popup-add-service');
					this._refreshContext();

					if (response.serviceId) {
						setTimeout(() => {
							this.openServiceEditor(table, response.serviceId, invoiceId);
						}, 300);
					}
				} else {
					alert(response.error || 'Error creating service');
				}
			}
		} catch (error) {
			console.error('[CRM] selectRoom error:', error);
		}
	}

	// ─── Notes ───────────────────────────────────────────────────────

	// Open notes popup. Optionally preselect a service (table + id) so a new
	// note gets attached to that service.
	async openNotes(invoiceId, serviceTable, serviceId) {
		try {
			const params = {
				action: 'loadNotes',
				id: invoiceId || this.invoiceId
			};
			if (serviceTable && serviceId) {
				params.serviceTable = serviceTable;
				params.serviceId = serviceId;
			}

			const response = await window.ajax.post('/ajax.php?mid=47', params, { dataType: 'html' });

			const wasOpen = !!document.querySelector('.popup-notes.active');
			this._updateOrInsertPopup('.popup-notes', response);

			const popup = document.querySelector('.popup-notes');
			if (popup) {
				// Remember preselected service for submit
				if (serviceTable && serviceId) {
					popup.dataset.serviceTable = serviceTable;
					popup.dataset.serviceId = serviceId;
				}
				if (!wasOpen) {
					this.bindEvents(popup);
					requestAnimationFrame(() => {
						popupManager.open('popup-notes');
					});
				}
			}
		} catch (error) {
			console.error('[CRM] openNotes error:', error);
		}
	}

	// Back-compat alias used by older templates
	async openNotesLog(invoiceId) {
		return this.openNotes(invoiceId);
	}

	async _refreshNotes() {
		const popup = document.querySelector('.popup-notes');
		if (!popup) return;
		const invoiceId = popup.getAttribute('data-invoice-id');
		const showResolved = popup.classList.contains('popup-notes--show-resolved');
		if (invoiceId) {
			await this.openNotes(invoiceId, popup.dataset.serviceTable, popup.dataset.serviceId);
			// Preserve the "show resolved" toggle across the refresh
			if (showResolved) {
				const fresh = document.querySelector('.popup-notes');
				if (fresh) {
					fresh.classList.add('popup-notes--show-resolved');
					const cb = fresh.querySelector('.popup-notes__filter-toggle input');
					if (cb) cb.checked = true;
				}
			}
		}
	}

	async submitNote(btn) {
		const popup = btn.closest('.popup-notes');
		if (!popup) return;

		const invoiceId = popup.getAttribute('data-invoice-id');
		const textarea = popup.querySelector('[name="text"]');
		const text = textarea ? textarea.value.trim() : '';

		if (!text) {
			alert('Please enter the note text');
			return;
		}

		const params = {
			action: 'addNote',
			invoiceId: invoiceId,
			text: text
		};
		if (popup.dataset.serviceTable && popup.dataset.serviceId) {
			params.serviceTable = popup.dataset.serviceTable;
			params.serviceId = popup.dataset.serviceId;
		}

		try {
			const response = await window.ajax.post('/ajax.php?mid=47', params);
			if (response.success) {
				await this._refreshNotes();
				this._refreshContext();
			} else {
				alert(response.error || 'Error adding note');
			}
		} catch (error) {
			console.error('[CRM] submitNote error:', error);
		}
	}

	toggleResolvedNotes(input) {
		const popup = input.closest('.popup-notes');
		if (!popup) return;
		popup.classList.toggle('popup-notes--show-resolved', input.checked);
	}

	async resolveNote(noteId) {
		await this._noteAction('resolveNote', noteId);
	}

	async restoreNote(noteId) {
		await this._noteAction('restoreNote', noteId);
	}

	async deleteNote(noteId) {
		if (!confirm('Delete this note?')) return;
		await this._noteAction('deleteNote', noteId);
	}

	async _noteAction(action, noteId) {
		// Blur the clicked button — morphdom skips patching document.activeElement,
		// which would otherwise leave the resolve/restore icon stale after refresh.
		if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
		try {
			const response = await window.ajax.post('/ajax.php?mid=47', { action, id: noteId });
			if (response.success) {
				await this._refreshNotes();
				this._refreshContext();
			} else {
				alert(response.error || 'Error');
			}
		} catch (error) {
			console.error('[CRM] ' + action + ' error:', error);
		}
	}

	// ─── Invoice files ─────────────────────────────────────────────────

	async openFiles(invoiceId) {
		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'loadFiles',
				id: invoiceId || this.invoiceId
			}, { dataType: 'html' });

			const wasOpen = !!document.querySelector('.popup-files.active');
			this._updateOrInsertPopup('.popup-files', response);

			if (!wasOpen) {
				const popup = document.querySelector('.popup-files');
				if (popup) {
					this.bindEvents(popup);
					requestAnimationFrame(() => {
						popupManager.open('popup-files');
					});
				}
			}
		} catch (error) {
			console.error('[CRM] openFiles error:', error);
		}
	}

	async _refreshFiles() {
		const popup = document.querySelector('.popup-files');
		if (!popup) return;
		const invoiceId = popup.getAttribute('data-invoice-id');
		if (invoiceId) await this.openFiles(invoiceId);
	}

	onFilePick(input) {
		const label = input.closest('.popup-files__file-pick');
		if (!label) return;
		const nameEl = label.querySelector('.popup-files__file-name');
		if (!nameEl) return;
		if (input.files && input.files[0]) {
			nameEl.textContent = input.files[0].name;
		} else {
			nameEl.textContent = nameEl.dataset.empty || 'Choose file...';
		}
	}

	async submitFile(btn) {
		const popup = btn.closest('.popup-files');
		if (!popup) return;

		const invoiceId = popup.getAttribute('data-invoice-id');
		const form = btn.closest('.popup-files__form');
		const fileInput = form.querySelector('[name="file"]');

		if (!fileInput || !fileInput.files || !fileInput.files[0]) {
			alert('Please choose a file');
			return;
		}

		const fd = new FormData();
		fd.append('action', 'addFile');
		fd.append('invoiceId', invoiceId);
		fd.append('name', form.querySelector('[name="name"]').value);
		fd.append('file', fileInput.files[0]);

		try {
			const response = await fetch('/ajax.php?mid=47', { method: 'POST', body: fd });
			const data = await response.json();
			if (data.success) {
				await this._refreshFiles();
			} else {
				alert(data.error || 'Error adding file');
			}
		} catch (error) {
			console.error('[CRM] submitFile error:', error);
		}
	}

	async deleteFile(fileId) {
		if (!confirm('Delete this file?')) return;

		try {
			const response = await window.ajax.post('/ajax.php?mid=47', {
				action: 'deleteFile',
				id: fileId
			});
			if (response.success) {
				await this._refreshFiles();
			} else {
				alert(response.error || 'Error deleting file');
			}
		} catch (error) {
			console.error('[CRM] deleteFile error:', error);
		}
	}

	// ─── Vouchers ──────────────────────────────────────────────────────

	viewVouchers(invoiceId) {
		console.log('[CRM] viewVouchers:', invoiceId);
		// Заглушка
	}

	// ─── Refresh related popups ───────────────────────────────────────

	async _refreshOpenServiceEditor() {
		const sePopup = document.querySelector('.popup-service-editor.active');
		if (!sePopup) return;

		const table = sePopup.getAttribute('data-table');
		const serviceId = sePopup.getAttribute('data-service-id');
		if (table && serviceId) {
			await this.openServiceEditor(table, serviceId, this.invoiceId);
		}
	}

	// ─── Helpers ───────────────────────────────────────────────────────

	/**
	 * Вставляет попап или обновляет через morphdom (без потери DOM-состояния)
	 */
	// Refresh open service editor via morphdom (debounced)
	_refreshTimer = null;
	_refreshOpenServiceEditor(table, serviceId) {
		clearTimeout(this._refreshTimer);
		this._refreshTimer = setTimeout(async () => {
			const sePopup = document.querySelector('.popup-service-editor.active');
			if (!sePopup) return;
			await this.openServiceEditor(table, serviceId, this.invoiceId);
		}, 200);
	}

	_updateOrInsertPopup(selector, html) {
		const existing = document.querySelector(selector);
		if (existing) {
			// morphdom-обновление — сохраняет DOM, фокус, классы
			const temp = document.createElement('div');
			temp.innerHTML = html.trim();
			const newEl = temp.firstElementChild;
			if (newEl) {
				// Сохраняем класс active если был
				if (existing.classList.contains('active')) {
					newEl.classList.add('active');
				}
				morphdom(existing, newEl, {
					onBeforeElUpdated(fromEl, toEl) {
						if (fromEl.hasAttribute && fromEl.hasAttribute('data-morph-skip')) return false;
						if (fromEl === document.activeElement) return false;
						// Skip open timepicker/datepicker popups
						if (fromEl.classList && fromEl.classList.contains('timepicker__popup')) return false;
						if (fromEl.classList && fromEl.classList.contains('flatpickr-calendar')) return false;
						// Preserve open multi-tag dropdowns
						if (fromEl.classList && fromEl.classList.contains('popup-se__multi-tag--open')) {
							toEl.classList.add('popup-se__multi-tag--open');
						}
						return true;
					},
					onBeforeNodeDiscarded(node) {
						if (node.hasAttribute && node.hasAttribute('data-morph-skip')) return false;
						// Preserve lazy-created popups
						if (node.classList && node.classList.contains('timepicker__popup')) return false;
						if (node.classList && node.classList.contains('flatpickr-calendar')) return false;
						return true;
					}
				});

			}
		} else {
			document.body.insertAdjacentHTML('beforeend', html);
		}
	}

	/**
	 * Привязка интерактивных элементов к контейнеру
	 */
	bindEvents(container) {
		initDatepickers(container);
		initFormMulti(container);
		popupManager.bindPopupEvents(container);
	}
}
